import { useCallback, useEffect, useRef, useState, Suspense } from 'react';
import html2canvas from 'html2canvas-pro';
import { type ProcessedImage, type TemplateProps } from '@/utils/types';
import { type UserData } from '@/types/userData';

export interface TemplateConfig {
    name: string;
    component: React.ComponentType<TemplateProps>;
    getProps: (userData: UserData) => TemplateProps;
    shouldInclude?: (userData: UserData) => boolean;
}

interface TemplateProcessorProps {
    templateConfig: TemplateConfig;
    userData: UserData;
    onComplete: (image: ProcessedImage) => void;
    onError: (error: Error) => void;
    autoProcess?: boolean;
    className?: string;
}

export const TemplateProcessor = ({
    templateConfig,
    userData,
    onComplete,
    onError,
    autoProcess = false,
    className = "fixed -left-[2000px] -top-[2000px] pointer-events-none"
}: TemplateProcessorProps) => {
    const [isProcessing, setIsProcessing] = useState<boolean>(false);
    const [isReady, setIsReady] = useState<boolean>(false);
    const templateRef = useRef<HTMLDivElement | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);

    // Helper function to wait for all images in SVG to load
    const waitForImagesInSVG = useCallback(async (element: HTMLElement): Promise<void> => {
        const svgElements = element.querySelectorAll('svg');
        const imagePromises: Promise<void>[] = [];
        let totalImages = 0;
        let dataUrlImages = 0;
        let externalImages = 0;

        svgElements.forEach(svg => {
            // Find all image elements within the SVG
            const images = svg.querySelectorAll('image[href], image[xlink\\:href]');
            totalImages += images.length;

            images.forEach(img => {
                const imageElement = img as SVGImageElement;
                const href = imageElement.href?.baseVal || imageElement.getAttribute('xlink:href');

                if (href && !href.startsWith('data:')) {
                    externalImages++;
                    // Only wait for external images, not data URLs
                    const promise = new Promise<void>((resolve) => {
                        const testImg = new Image();
                        testImg.crossOrigin = 'anonymous';

                        testImg.onload = () => {
                            console.log('✓ External image loaded:', href.substring(0, 50) + '...');
                            resolve();
                        };
                        testImg.onerror = () => {
                            console.warn('✗ Failed to load image:', href);
                            resolve(); // Resolve anyway to not block the process
                        };

                        testImg.src = href;
                    });

                    imagePromises.push(promise);
                } else if (href?.startsWith('data:')) {
                    dataUrlImages++;
                }
            });
        });

        console.log(`[Image Check] Total: ${totalImages}, Data URLs: ${dataUrlImages}, External: ${externalImages}`);

        // Wait for all images to load
        if (imagePromises.length > 0) {
            await Promise.all(imagePromises);
            console.log(`✓ All ${imagePromises.length} external images loaded`);
        }
    }, []);

    // Helper function to wait for fonts to load
    const waitForFonts = useCallback(async (): Promise<void> => {
        console.log('[Font Check] Waiting for fonts...');
        if ('fonts' in document && document.fonts.ready) {
            await document.fonts.ready;
            console.log('✓ Fonts ready');
        } else {
            console.log('⚠ Font API not available');
        }
    }, []);

    // Helper function to ensure DOM is fully rendered
    const waitForDOMRender = useCallback(async (): Promise<void> => {
        return new Promise<void>((resolve) => {
            // Use requestAnimationFrame to wait for the next paint
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    resolve();
                });
            });
        });
    }, []);

    // Helper function to ensure SVG is fully rendered and sized correctly
    const waitForSVGRender = useCallback(async (element: HTMLElement): Promise<void> => {
        const svgElements = element.querySelectorAll('svg');
        console.log(`[SVG Render Check] Found ${svgElements.length} SVG element(s)`);

        return new Promise<void>((resolve) => {
            let pendingSVGs = svgElements.length;

            if (pendingSVGs === 0) {
                console.log('✓ No SVG elements to check');
                resolve();
                return;
            }

            const maxAttempts = 120; // 2 seconds at 60fps
            let attempts = 0;
            let lastLogAttempt = 0;

            svgElements.forEach((svg, svgIndex) => {
                const checkSVGReady = () => {
                    attempts++;

                    // Check if SVG has proper dimensions
                    const rect = svg.getBoundingClientRect();
                    const hasValidDimensions = rect.width > 0 && rect.height > 0;

                    // Check if all child elements are rendered
                    const hasChildren = svg.children.length > 0;

                    // Check if SVG images have valid href attributes (dimensions aren't reliable)
                    const svgImages = svg.querySelectorAll('image');
                    let allImagesValid = true;
                    let imageDetails = '';
                    if (svgImages.length > 0) {
                        svgImages.forEach((img, idx) => {
                            const imageElement = img as SVGImageElement;
                            const href = imageElement.href?.baseVal || imageElement.getAttribute('xlink:href');
                            const width = imageElement.getAttribute('width');
                            const height = imageElement.getAttribute('height');
                            
                            // Image is valid if it has href and width/height attributes
                            const isValid = href && href.length > 0 && width && height;
                            
                            if (!isValid) {
                                allImagesValid = false;
                                imageDetails += `img${idx}:${isValid ? 'ok' : 'invalid'} `;
                            }
                        });
                    }

                    // Check if text elements have been rendered with fonts (if any exist with content)
                    const textElements = svg.querySelectorAll('text');
                    let allTextRendered = true;
                    let textRenderDetails = '';
                    if (textElements.length > 0) {
                        textElements.forEach((text, idx) => {
                            // Only check text elements that have content
                            if (text.textContent && text.textContent.trim().length > 0) {
                                const textRect = text.getBoundingClientRect();
                                if (textRect.width === 0 || textRect.height === 0) {
                                    allTextRendered = false;
                                    textRenderDetails += `text${idx}:${textRect.width}x${textRect.height} `;
                                }
                            }
                        });
                    }

                    const isReady = hasValidDimensions && hasChildren && allImagesValid && allTextRendered;

                    // Log every 30 attempts (roughly every 500ms at 60fps)
                    if (attempts - lastLogAttempt >= 30 && !isReady) {
                        lastLogAttempt = attempts;
                        console.log(`[SVG ${svgIndex}] Attempt ${attempts}: dims=${hasValidDimensions}(${rect.width}x${rect.height}), children=${hasChildren}(${svg.children.length}), images=${allImagesValid}(${svgImages.length}) ${imageDetails}, text=${allTextRendered}(${textElements.length}) ${textRenderDetails}`);
                    }

                    if (isReady || attempts >= maxAttempts) {
                        if (attempts >= maxAttempts) {
                            console.warn(`⚠ SVG ${svgIndex} render check timed out after ${attempts} attempts. Status: dims=${hasValidDimensions}, images=${allImagesValid}, text=${allTextRendered}. Proceeding anyway.`);
                        } else {
                            console.log(`✓ SVG ${svgIndex} ready after ${attempts} attempts`);
                        }
                        pendingSVGs--;
                        if (pendingSVGs === 0) {
                            console.log('✓ All SVGs rendered');
                            resolve();
                        }
                    } else {
                        // Try again on next frame
                        requestAnimationFrame(checkSVGReady);
                    }
                };

                checkSVGReady();
            });
        });
    }, []);

    // Helper function to ensure browser has painted SVG images after loading
    const waitForSVGImagePaint = useCallback(async (element: HTMLElement): Promise<void> => {
        const svgImages = element.querySelectorAll('svg image');
        
        console.log(`[Paint Check] Waiting for ${svgImages.length} SVG image(s) to paint...`);
        
        if (svgImages.length === 0) {
            console.log('✓ No SVG images to paint');
            return Promise.resolve();
        }
        
        // Wait 3 animation frames to ensure browser has painted the images
        return new Promise<void>((resolve) => {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        console.log('✓ SVG images painted (3 frames elapsed)');
                        resolve();
                    });
                });
            });
        });
    }, []);

    const processTemplate = useCallback(async (): Promise<void> => {
        if (isProcessing || !templateRef.current) {
            return;
        }

        console.log(`\n========== PROCESSING TEMPLATE: ${templateConfig.name} ==========`);
        setIsProcessing(true);
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;

        try {
            // Use requestIdleCallback to process during idle time, fallback to setTimeout
            const scheduleWork = (callback: () => void): void => {
                if ('requestIdleCallback' in window) {
                    requestIdleCallback(callback, { timeout: 1000 });
                } else {
                    setTimeout(callback, 0);
                }
            };

            // Wait for DOM to be fully rendered
            console.log('[Step 1/6] Waiting for DOM render...');
            await waitForDOMRender();
            console.log('✓ DOM rendered');

            if (signal.aborted) {
                throw new Error('Processing aborted');
            }

            // Wait for fonts to load
            console.log('[Step 2/6] Checking fonts...');
            await waitForFonts();

            if (signal.aborted) {
                throw new Error('Processing aborted');
            }

            // Wait for all images in SVG to load
            console.log('[Step 3/6] Loading SVG images...');
            await waitForImagesInSVG(templateRef.current);

            if (signal.aborted) {
                throw new Error('Processing aborted');
            }

            // Wait for browser to paint the loaded images
            console.log('[Step 4/6] Waiting for image paint...');
            await waitForSVGImagePaint(templateRef.current);

            if (signal.aborted) {
                throw new Error('Processing aborted');
            }

            // Wait for SVG to be fully rendered with proper dimensions
            console.log('[Step 5/6] Verifying SVG render...');
            await waitForSVGRender(templateRef.current);

            if (signal.aborted) {
                throw new Error('Processing aborted');
            }

            // Add a minimal safety buffer to ensure everything is fully painted
            console.log('[Step 6/6] Safety buffer (100ms)...');
            await new Promise<void>((resolve) => setTimeout(resolve, 500));

            if (signal.aborted) {
                throw new Error('Processing aborted');
            }

            console.log(`✓ All render checks passed - Starting html2canvas capture`);

            // Process with html2canvas in a non-blocking way
            const canvas = await new Promise<HTMLCanvasElement>((resolve, reject) => {
                scheduleWork(async () => {
                    try {
                        if (signal.aborted) {
                            reject(new Error('Processing aborted'));
                            return;
                        }

                        const result = await html2canvas(templateRef.current!, {
                            width: 1080,
                            height: 1350,
                            scale: 1,
                            useCORS: true,
                            allowTaint: true,
                            backgroundColor: '#ffffff',
                            logging: false,
                            removeContainer: true,
                            imageTimeout: 15000, // Allow time for images to load in cloned document
                            onclone: (clonedDoc: Document) => {
                                // Ensure cloned SVG elements preserve their styling and attributes
                                const clonedSVGs = clonedDoc.querySelectorAll('svg');
                                clonedSVGs.forEach(svg => {
                                    // Force the SVG to render by setting display property
                                    const svgElement = svg as SVGSVGElement;
                                    svgElement.style.display = 'block';
                                });
                            }
                        });

                        resolve(result);
                    } catch (error) {
                        reject(error);
                    }
                });
            });

            if (signal.aborted) {
                throw new Error('Processing aborted');
            }

            console.log('✓ html2canvas capture complete - Converting to data URL...');

            // Convert to data URL in a non-blocking way
            const dataUrl = await new Promise<string>((resolve) => {
                scheduleWork(() => {
                    const result = canvas.toDataURL('image/jpeg', 0.9);
                    resolve(result);
                });
            });

            if (signal.aborted) {
                throw new Error('Processing aborted');
            }

            const processedImage: ProcessedImage = {
                dataUrl,
                templateName: templateConfig.name,
            };

            console.log(`✓ Template processing complete: ${templateConfig.name} (${Math.round(dataUrl.length / 1024)}KB)`);
            console.log(`========== END ${templateConfig.name} ==========\n`);

            onComplete(processedImage);
        } catch (error) {
            if (signal.aborted) {
                console.log('⚠ Template processing was aborted');
                return;
            }

            const processingError = error instanceof Error
                ? error
                : new Error(`Failed to process template ${templateConfig.name}`);

            console.error(`✗ ERROR processing template ${templateConfig.name}:`, processingError);
            console.log(`========== ERROR ${templateConfig.name} ==========\n`);
            onError(processingError);
        } finally {
            setIsProcessing(false);
            abortControllerRef.current = null;
        }
    }, [templateConfig, onComplete, onError, isProcessing, waitForDOMRender, waitForFonts, waitForImagesInSVG, waitForSVGImagePaint, waitForSVGRender]);

    // Set ready state when component mounts and ref is available
    useEffect(() => {
        if (templateRef.current) {
            // Small delay to ensure DOM is fully rendered
            const timer = setTimeout(() => {
                setIsReady(true);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, []);

    // Auto-process when ready if autoProcess is enabled
    useEffect(() => {
        if (autoProcess && isReady && !isProcessing) {
            processTemplate();
        }
    }, [autoProcess, isReady, isProcessing, processTemplate]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, []);


    // Get props for the template component
    const templateProps = templateConfig.getProps(userData);
    const Component = templateConfig.component;

    return (
        <div
            ref={templateRef}
            className={`w-[1080px] h-[1350px] ${className}`}
        >
            <Suspense fallback={<div className="w-[1080px] h-[1350px] bg-gray-100" />}>
                <Component {...templateProps} />
            </Suspense>
        </div>
    );
};
